import { useCallback, useMemo } from 'react';
import { V2_PATHS, useResourcesConfig } from '../config/ResourcesConfigContext';
import { toPayload, type AuthoringKind, type ResourceDraft } from './types';

const PATH_BY_KIND: Record<AuthoringKind, string> = {
    faq: V2_PATHS.faqs,
    video: V2_PATHS.videos,
    helpDocument: V2_PATHS.helpDocuments
};

const FILES_PATH = '/content/files';

/** Server ceiling for a help-document PDF (see files.controller.ts). */
export const MAX_PDF_BYTES = 25 * 1024 * 1024;

interface UploadResponse {
    fileId: string;
}

interface Envelope<T> {
    success?: boolean;
    data?: T;
}

/** Same tolerance as `unwrap` in hooks/useContentFiles: the dashboard's client
 * unwraps the `{ success, data }` envelope, files-viewer's does not. */
function unwrap<T>(responseData: T | Envelope<T>): T | null {
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
        return (responseData as Envelope<T>).data ?? null;
    }
    return (responseData as T) ?? null;
}

/** Failed-write message: v2 AppError envelope, then plain message, then the fallback. */
export function writeErrorMessage(error: unknown, fallback: string): string {
    const response = (error as { response?: { data?: unknown } })?.response;
    const data = response?.data as { error?: { message?: string }; message?: string } | undefined;
    return data?.error?.message ?? data?.message ?? fallback;
}

export interface ResourceWrites {
    /** Creates or updates, uploading a newly picked PDF first. Returns nothing;
     * callers refresh the library rather than patching local state. */
    save: (draft: ResourceDraft) => Promise<void>;
    remove: (kind: AuthoringKind, id: number) => Promise<void>;
}

export function useResourceWrites(): ResourceWrites {
    const { api } = useResourcesConfig();

    const uploadPdf = useCallback(
        async (file: File): Promise<string> => {
            const form = new FormData();
            form.append('kind', 'help-document');
            form.append('file', file);
            // axios v1 JSON-serialises FormData under the hosts' default
            // application/json - force multipart so the file part survives.
            const response = await api.post<UploadResponse | Envelope<UploadResponse>>(
                FILES_PATH,
                form,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const fileId = unwrap<UploadResponse>(response.data)?.fileId;
            if (!fileId) {
                throw new Error('The upload did not return a file id.');
            }
            return fileId;
        },
        [api]
    );

    const save = useCallback(
        async (draft: ResourceDraft): Promise<void> => {
            // Upload first: a failed row write only orphans a GCS object, never
            // the reverse.
            const uploadedFileId = draft.pdfFile ? await uploadPdf(draft.pdfFile) : null;
            const body = toPayload(draft, uploadedFileId);
            const path = PATH_BY_KIND[draft.kind];

            if (draft.id == null) {
                await api.post(path, body);
                return;
            }
            await api.put(`${path}/${draft.id}`, body);
        },
        [api, uploadPdf]
    );

    const remove = useCallback(
        async (kind: AuthoringKind, id: number): Promise<void> => {
            await api.delete(`${PATH_BY_KIND[kind]}/${id}`);
        },
        [api]
    );

    return useMemo<ResourceWrites>(() => ({ save, remove }), [save, remove]);
}
